import React, { useEffect, useMemo, useState, useRef } from "react";
import {
  uploadToStorage,
  saveMarketplaceDraft,
  publishMarketplaceItem,
  extractConfigSnapshotFromModele,
} from "../services/data";
import { db, storage } from "../firebase/firebase";
import { doc, getDoc } from "firebase/firestore";
import { ref as sRef } from "firebase/storage";

async function buildModeleAssetsForPayload({ uid, sourceModeleId }) {
  if (!uid || !sourceModeleId) return {};
  const snap = await getDoc(doc(db, "users", uid, "modeles", sourceModeleId));
  if (!snap.exists()) return {};

  const m = snap.data() || {};
  let templatePath = m.storagePath || null;
  if (!templatePath && m.fileURL) {
    try {
      const ref = sRef(storage, m.fileURL);
      templatePath = ref.fullPath || m.fileURL;
    } catch {
      templatePath = m.fileURL;
    }
  }

  const configSnapshot = await extractConfigSnapshotFromModele(m);
  const configSourceKind =
    (m.configInline && "configInline") ||
    (m.fields_meta && "fields_meta") ||
    (m.configPath && "configPath") ||
    (m.configURL && "configURL") ||
    ((m.docxMeta || m.docx_meta) && "docxMeta") ||
    null;
  const templateMeta = m.docxMeta || m.docx_meta || null;

  const extra = {
    sourceModeleId,
    configSnapshot,
    configSourceKind,
    templateMeta,
    configPath: m.configPath || null,
    configURL: m.configURL || null,
  };
  if (templatePath) extra.templatePath = templatePath;
  return extra;
}

/**
 * Page publication marketplace (UI only, sans persistance).
 * Reçoit depuis App.jsx :
 *   - user
 *   - modele (métadonnées de base, ex. .nom)
 *   - draft (optionnel)
 *   - onBack()
 *   - onDone()  -> à brancher plus tard sur Firestore (draft/publish)
 */
export default function MarketplacePublish({
  user,
  modele,
  draft,
  onBack,
  onDone,
}) {
  // Champs de base
  const [title, setTitle] = useState(modele?.nom || "");
  const [subtitle, setSubtitle] = useState("");
  const [category, setCategory] = useState("");
  const [subCategory, setSubCategory] = useState("");
  const [languages, setLanguages] = useState([]); // ex. ["FR","AR"]
  const [jurisdictions, setJurisdictions] = useState([]); // ex. ["MA","FR"]

  // Monétisation / licence
  const [isPremium, setIsPremium] = useState(false);
  const [price, setPrice] = useState("");
  const [currency, setCurrency] = useState("MAD");
  const [licenseType, setLicenseType] = useState("usage-pro"); // usage-pro | usage-perso | libre

  // Présentation
  const [description, setDescription] = useState("");
  const [instructions, setInstructions] = useState("");
  const [tags, setTags] = useState(""); // séparés par virgules

  // UX/complexité
  const [complexity, setComplexity] = useState("intermediaire"); // debutant | intermediaire | avance
  const [fillTime, setFillTime] = useState("6–10 min");

  // Flags fonctionnalités
  const [flags, setFlags] = useState({
    formulas: false,
    repeats: false,
    images: false,
    signature: false,
    multilanguage: true,
    exportPdf: true,
    exportDocx: true,
  });

  // Médias (vignettes / captures) – UI seulement
  const [thumb, setThumb] = useState(null); // DataURL ou File (placeholder)
  const [gallery, setGallery] = useState([]); // array de DataURL / File (placeholder)
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);

  const langOptions = ["FR", "AR", "EN"];
  const jurOptions = ["MA", "FR", "EU"];
  const resolvedSourceModeleId = draft?.sourceModeleId || modele?.id || null;

  // Validation minimale pour (Publier) – tu peux durcir plus tard
  const checklistOk = useMemo(() => {
    if (!title?.trim()) return false;
    if (!category?.trim()) return false;
    if (languages.length === 0) return false;
    if (jurisdictions.length === 0) return false;
    if (isPremium && (price === "" || Number(price) < 0)) return false;
    if (!description || description.length < 50) return false; // mini utile
    return true;
  }, [
    title,
    category,
    languages,
    jurisdictions,
    isPremium,
    price,
    description,
  ]);

  const parsedTags = useMemo(
    () =>
      Array.from(
        new Set(
          (tags || "")
            .split(",")
            .map((t) => t.trim())
            .filter(Boolean)
            .slice(0, 10) // anti-spam
        )
      ),
    [tags]
  );

  const toggleSet = (value, list, setter) => {
    if (list.includes(value)) setter(list.filter((v) => v !== value));
    else setter([...list, value]);
  };

  const toggleFlag = (k) => setFlags((f) => ({ ...f, [k]: !f[k] }));

  // Refs inputs fichier
  const thumbInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  // Pré-remplir depuis un brouillon existant
  useEffect(() => {
    if (!draft) return;
    setTitle(draft.title || "");
    setSubtitle(draft.subtitle || "");
    setCategory(draft.category || "");
    setSubCategory(draft.subCategory || "");
    setLanguages(Array.isArray(draft.languages) ? draft.languages : []);
    setJurisdictions(
      Array.isArray(draft.jurisdictions) ? draft.jurisdictions : []
    );
    setIsPremium(!!draft.isPremium);
    setPrice(draft.price ?? "");
    setCurrency(draft.currency || "MAD");
    setLicenseType(draft.licenseType || "usage-pro");
    setDescription(draft.description || "");
    setInstructions(draft.instructions || "");
    setTags(
      Array.isArray(draft.tags) ? draft.tags.join(", ") : draft.tags || ""
    );
    setComplexity(draft.complexity || "intermediaire");
    setFillTime(draft.fillTime || "6–10 min");
    setFlags({
      formulas: !!draft?.flags?.formulas,
      repeats: !!draft?.flags?.repeatSections,
      images: !!draft?.flags?.images,
      signature: !!draft?.flags?.esign,
      multilanguage: !!draft?.flags?.i18n,
      exportPdf: !!draft?.flags?.pdfExport,
      exportDocx: !!draft?.flags?.docxExport,
    });
    if (draft.thumbPath) {
      setThumb({
        src: null,
        name: draft.thumbPath.split("/").pop() || "thumb",
        file: null,
        existingPath: draft.thumbPath,
      });
    } else {
      setThumb(null);
    }
    if (Array.isArray(draft.galleryPaths) && draft.galleryPaths.length) {
      setGallery(
        draft.galleryPaths.map((p, idx) => ({
          src: null,
          name: p?.split("/").pop() || `gallery-${idx + 1}`,
          file: null,
          existingPath: p,
        }))
      );
    } else {
      setGallery([]);
    }
  }, [draft]);

  // Helpers lecture fichier(s) -> DataURL
  const readAsDataURL = (file) =>
    new Promise((resolve, reject) => {
      const fr = new FileReader();
      fr.onload = (e) => resolve(e.target.result);
      fr.onerror = reject;
      fr.readAsDataURL(file);
    });

  const uploadThumbIfAny = async () => {
    if (!user?.uid) return thumb?.existingPath || null;
    if (thumb?.existingPath && !thumb?.file) return thumb.existingPath;
    if (!thumb?.file) return null;
    const { storagePath } = await uploadToStorage({
      uid: user.uid,
      file: thumb.file,
      folder: "marketplace",
      contentType: thumb.file.type,
    });
    return storagePath;
  };

  const uploadGalleryIfAny = async () => {
    if (!user?.uid || !gallery?.length) return [];
    const out = [];
    for (const g of gallery) {
      if (g?.existingPath && !g?.file) {
        out.push(g.existingPath);
        continue;
      }
      if (!g?.file) continue;
      const { storagePath } = await uploadToStorage({
        uid: user.uid,
        file: g.file,
        folder: "marketplace",
        contentType: g.file.type,
      });
      out.push(storagePath);
    }
    return out;
  };

  const buildPayload = async () => {
    const thumbPath = await uploadThumbIfAny();
    const galleryPaths = await uploadGalleryIfAny();
    const normalizedTags = Array.from(
      new Set(
        (tags || "")
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean)
      )
    );
    return {
      title: title?.trim() || "",
      subtitle: subtitle?.trim() || "",
      category: category || "",
      subCategory: subCategory || "",
      languages,
      jurisdictions,
      isPremium,
      price: isPremium ? Number(price) : 0,
      currency: currency || "MAD",
      licenseType,
      description: description || "",
      instructions: instructions || "",
      tags: normalizedTags,
      complexity,
      fillTime,
      flags: {
        formulas: !!flags.formulas,
        repeatSections: !!flags.repeats,
        images: !!flags.images,
        esign: !!flags.signature,
        i18n: !!flags.multilanguage,
        pdfExport: !!flags.exportPdf,
        docxExport: !!flags.exportDocx,
      },
      sourceModeleId: resolvedSourceModeleId,
      thumbPath: thumbPath || null,
      galleryPaths,
    };
  };

  // VIGNETTE
  const handleThumbInput = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const src = await readAsDataURL(f);
    setThumb({ src, name: f.name, file: f, existingPath: null });
  };

  const handleThumbDrop = async (e) => {
    e.preventDefault();
    const f = e.dataTransfer?.files?.[0];
    if (!f) return;
    const src = await readAsDataURL(f);
    setThumb({ src, name: f.name, file: f, existingPath: null });
  };

  // GALERIE
  const handleGalleryInput = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const items = await Promise.all(
      files.map(async (f) => ({
        src: await readAsDataURL(f),
        name: f.name,
        file: f,
        existingPath: null,
      }))
    );
    setGallery((prev) => [...prev, ...items].slice(0, 8)); // limite douce à 8
  };

  const removeThumb = () => setThumb(null);
  const removeGalleryAt = (idx) =>
    setGallery((prev) => prev.filter((_, i) => i !== idx));

  const onSaveDraft = async () => {
    if (!user?.uid) return;
    setSaving(true);
    try {
      const payload = await buildPayload();
      await saveMarketplaceDraft(user.uid, {
        id: draft?.id,
        ...payload,
        status: "draft",
      });
      alert("Brouillon enregistre.");
      onDone && onDone();
    } catch (e) {
      console.error(e);
      alert("Echec de l'enregistrement du brouillon");
    } finally {
      setSaving(false);
    }
  };

  const onPublish = async () => {
    if (!user?.uid) {
      alert("Connecte-toi.");
      return;
    }
    if (!checklistOk) {
      alert("Complete les champs obligatoires avant de publier.");
      return;
    }
    if (!resolvedSourceModeleId) {
      alert("Sélectionne le modèle source.");
      return;
    }
    setPublishing(true);
    try {
      let payload = await buildPayload();
      const extra = await buildModeleAssetsForPayload({
        uid: user.uid,
        sourceModeleId: resolvedSourceModeleId,
      });
      payload = {
        ...payload,
        ...extra,
      };

      await publishMarketplaceItem(user.uid, payload);
      alert("Envoye pour validation (pending_review).");
      onDone && onDone();
    } catch (e) {
      console.error(e);
      alert("Echec de la publication");
    } finally {
      setPublishing(false);
    }
  };

    return (
    <div className="marketplace-publish config-body">
      <div className="page-header">
        <h1 className="page-title">
          {draft ? "Modifier le brouillon" : "Publier au marketplace"}
        </h1>
        <div
          className="page-actions"
          style={{ display: "flex", gap: 8, margin: "8px 0 16px" }}
        >
          {onBack && (
            <button className="btn btn-outline" onClick={onBack}>
              <i className="fas fa-arrow-left" /> Retour
            </button>
          )}
          <button
            className="btn btn-outline"
            onClick={onSaveDraft}
            disabled={saving || publishing}
          >
            <i className="fas fa-save" /> Enregistrer brouillon
          </button>
          <button
            className="btn btn-primary"
            onClick={onPublish}
            disabled={publishing || !checklistOk}
            title={
              !checklistOk
                ? "Complète les champs obligatoires"
                : "Envoyer pour validation"
            }
          >
            {publishing ? "Envoi…" : "Publier"}
          </button>
        </div>
      </div>

      {/* Bloc informations principales */}
      <div className="config-card">
        <div className="section-header">
          <i className="fas fa-info-circle" /> Informations principales
        </div>
        <div className="config-grid">
          <div className="form-group">
            <label className="form-label">
              Titre <span className="badge-req">requis</span>
            </label>
            <input
              className="form-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ex. Contrat CDD – Maroc"
            />
            <div className="muted">
              Court et orienté résultat (≤ 60 caractères).
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Sous-titre</label>
            <input
              className="form-input"
              value={subtitle}
              onChange={(e) => setSubtitle(e.target.value)}
              placeholder="Ex. Prêt en 8 minutes, champs guidés"
            />
          </div>
          <div className="form-group">
            <label className="form-label">
              Catégorie <span className="badge-req">requis</span>
            </label>
            <select
              className="form-select"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              <option value="">— Choisir —</option>
              <option>Notaire</option>
              <option>RH</option>
              <option>Commercial</option>
              <option>Fiscalité</option>
              <option>Autre</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Sous-catégorie</label>
            <input
              className="form-input"
              value={subCategory}
              onChange={(e) => setSubCategory(e.target.value)}
              placeholder="Ex. Contrats de travail"
            />
      </div>
    </div>
  </div>

  {/* Localisation / langues */}
      <div className="config-card">
        <div className="section-header">
          <i className="fas fa-globe-africa" /> Langue(s) & juridictions
        </div>
        <div className="config-grid">
          <div className="form-group">
            <label className="form-label">
              Langues <span className="badge-req">requis</span>
            </label>
            <div className="chip-row">
              {langOptions.map((l) => (
                <button
                  key={l}
                  type="button"
                  className={`chip ${languages.includes(l) ? "active" : ""}`}
                  onClick={() => toggleSet(l, languages, setLanguages)}
                >
                  {l}
                </button>
              ))}
            </div>
            <div className="muted">
              Tu peux en cocher plusieurs (ex. FR + AR).
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">
              Pays / Juridictions <span className="badge-req">requis</span>
            </label>
            <div className="chip-row">
              {jurOptions.map((j) => (
                <button
                  key={j}
                  type="button"
                  className={`chip ${
                    jurisdictions.includes(j) ? "active" : ""
                  }`}
                  onClick={() => toggleSet(j, jurisdictions, setJurisdictions)}
                >
                  {j}
                </button>
              ))}
            </div>
            <div className="muted">
              Indique où le template est pertinent (ex. Maroc).
            </div>
          </div>
        </div>
      </div>

      {/* Monétisation */}
      <div className="config-card">
        <div className="section-header">
          <i className="fas fa-coins" /> Monétisation & licence
        </div>

        <div className="form-grid form-grid-3">
          <div className="form-group">
            <label className="form-label">Type</label>
            <div className="chip-row">
              <button
                type="button"
                className={`chip ${!isPremium ? "active" : ""}`}
                onClick={() => setIsPremium(false)}
              >
                Gratuit
              </button>
              <button
                type="button"
                className={`chip ${isPremium ? "active" : ""}`}
                onClick={() => setIsPremium(true)}
              >
                Premium
              </button>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">
              Prix{" "}
              {isPremium ? (
                <span className="badge-req">requis</span>
              ) : (
                <span className="badge-opt">optionnel</span>
              )}
            </label>
            <div className="grid-2">
              <input
                className="form-input"
                type="number"
                min="0"
                step="1"
                disabled={!isPremium}
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder="0"
              />
              <select
                className="form-select"
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
              >
                <option>MAD</option>
                <option>EUR</option>
                <option>USD</option>
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Licence</label>
            <select
              className="form-select"
              value={licenseType}
              onChange={(e) => setLicenseType(e.target.value)}
            >
              <option value="usage-pro">Usage professionnel</option>
              <option value="usage-perso">Usage personnel</option>
              <option value="libre">Libre (avec attribution)</option>
            </select>
            <div className="muted">
              Tu pourras préciser le texte légal plus tard.
            </div>
          </div>
        </div>
      </div>

      {/* Description / instructions */}
      <div className="config-card">
        <div className="section-header">
          <i className="fas fa-align-left" /> Description & instructions
        </div>
        <div className="config-grid">
          <div className="form-group full-width">
            <label className="form-label">
              Description <span className="badge-req">requis</span>
            </label>
            <textarea
              className="form-textarea"
              rows={6}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Explique le résultat pour l’utilisateur, les bénéfices, et le contexte d’usage..."
            />
            <div className="muted">
              {`${description.length}/500+`} – vise au moins 50 caractères pour
              aider la recherche.
            </div>
          </div>
          <div className="form-group full-width">
            <label className="form-label">Instructions</label>
            <textarea
              className="form-textarea"
              rows={5}
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              placeholder="Étapes d’utilisation rapides (3–5 points)..."
            />
          </div>
        </div>
      </div>

      {/* Tags, complexité, temps */}
      <div className="config-card">
        <div className="section-header">
          <i className="fas fa-filter" /> Découverte & difficulté
        </div>
        <div className="form-grid form-grid-3">
          <div className="form-group">
            <label className="form-label">Tags (séparés par virgules)</label>
            <input
              className="form-input"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="ex: contrat, cdd, maroc, rh"
            />
            {parsedTags.length > 0 && (
              <div className="tag-cloud">
                {parsedTags.map((t) => (
                  <span className="tag" key={t}>
                    {t}
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className="form-group">
            <label className="form-label">Complexité</label>
            <select
              className="form-select"
              value={complexity}
              onChange={(e) => setComplexity(e.target.value)}
            >
              <option value="debutant">Débutant</option>
              <option value="intermediaire">Intermédiaire</option>
              <option value="avance">Avancé</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Temps estimé</label>
            <input
              className="form-input"
              value={fillTime}
              onChange={(e) => setFillTime(e.target.value)}
              placeholder="ex: 6–10 min"
            />
          </div>
        </div>
      </div>

      {/* Fonctionnalités */}
      <div className="config-card">
        <div className="section-header">
          <i className="fas fa-puzzle-piece" /> Fonctionnalités du template
        </div>
        <div className="flag-grid">
          {[
            ["formulas", "Formules (SUM/IF/CONCAT/NUMTOTEXT/DATETOTEXT)"],
            ["repeats", "Sections répétées"],
            ["images", "Images / signatures {%...%}"],
            ["signature", "Signature électronique"],
            ["multilanguage", "Multi-langue"],
            ["exportPdf", "Export PDF"],
            ["exportDocx", "Export DOCX"],
          ].map(([k, label]) => (
            <label
              key={k}
              className={`checkbox-chip ${flags[k] ? "active" : ""}`}
            >
              <input
                type="checkbox"
                checked={!!flags[k]}
                onChange={() => toggleFlag(k)}
              />
              <span>{label}</span>
            </label>
          ))}
        </div>
        <div className="help-text">
          <i className="fas fa-info-circle" />
          Ces flags aident la recherche et les filtres.
        </div>
      </div>

      {/* Médias */}
      <div className="config-card">
        <div className="section-header">
          <i className="fas fa-images" /> Vignette & galerie
        </div>
        <div className="config-grid">
          <div className="form-group">
            <label className="form-label">Vignette</label>
            <div
              className={`thumb-uploader ${thumb ? "has-image" : ""}`}
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleThumbDrop}
              onClick={() => thumbInputRef.current?.click()}
              role="button"
              tabIndex={0}
            >
              {thumb ? (
                <>
                  {thumb.src ? (
                    <img className="thumb-img" src={thumb.src} alt="Vignette" />
                  ) : (
                    <div className="thumb-existing-label">
                      <i className="fas fa-image" /> Vignette existante
                    </div>
                  )}
                  <button
                    type="button"
                    className="thumb-remove"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeThumb();
                    }}
                    aria-label="Supprimer la vignette"
                  >
                    ×
                  </button>
                </>
              ) : (
                <>
                  <i className="fas fa-image" />
                  <span>Cliquer ou glisser-déposer une image (JPG/PNG)</span>
                </>
              )}
              <input
                ref={thumbInputRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={handleThumbInput}
              />
            </div>
          </div>
          <div className="form-group full-width">
            <label className="form-label">Galerie</label>
            <div className="thumb-grid">
              {gallery.map((g, i) => (
                <div key={`${g.name || g.existingPath}-${i}`} className="thumb">
                  {g.src ? (
                    <img
                      className="thumb-img"
                      src={g.src}
                      alt={`Capture ${i + 1}`}
                    />
                  ) : (
                    <div className="thumb-existing-label">
                      <i className="fas fa-images" /> Capture existante
                    </div>
                  )}
                  <button
                    type="button"
                    className="thumb-remove"
                    onClick={() => removeGalleryAt(i)}
                    aria-label={`Supprimer capture ${i + 1}`}
                  >
                    ×
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="thumb thumb-add"
                onClick={() => galleryInputRef.current?.click()}
              >
                <i className="fas fa-plus" />
                <span>Ajouter</span>
              </button>
              <input
                ref={galleryInputRef}
                type="file"
                accept="image/*"
                multiple
                style={{ display: "none" }}
                onChange={handleGalleryInput}
              />
            </div>
            <div className="muted">
              Ajoute 2–3 captures du formulaire et 1 de la sortie PDF/DOCX.
            </div>
          </div>
        </div>
      </div>

      {/* Récap note / futur QA */}
      <div className="config-card">
        <div className="section-header">
          <i className="fas fa-clipboard-check" /> Checklist (résumé)
        </div>
        <ul className="qa-list">
          <li className={title ? "ok" : "ko"}>Titre présent</li>
          <li className={category ? "ok" : "ko"}>Catégorie choisie</li>
          <li className={languages.length ? "ok" : "ko"}>Au moins 1 langue</li>
          <li className={jurisdictions.length ? "ok" : "ko"}>
            Au moins 1 juridiction
          </li>
          <li className={!isPremium || price !== "" ? "ok" : "ko"}>
            Prix si Premium
          </li>
          <li className={description?.length >= 50 ? "ok" : "ko"}>
            Description ≥ 50 caractères
          </li>
        </ul>
      </div>
    </div>
  );
}
